import type { ProductNoEdibleInfo as Product } from "@dransay/api";
import {
	CATEGORIES,
	type Cart,
	type CurrentCustomer,
	type CustomerOrderDetails,
	DELIVERY_METHOD_KIND,
	type DeliveryMethodKind,
	PRICES,
	type ProductsSortableField,
	type ProductsSortOrder,
	type ProductVendorListItem,
	type VoucherDetails,
} from "@dransay/api/models";
import * as Sentry from "@sentry/react-router";
import capitalize from "lodash/capitalize";
import isNil from "lodash/isNil";
import type { CheckoutStep } from "~/features/checkout/checkout.schemas";
import { CROSS_SELL_CATEGORY_SLUGS } from "~/features/checkout/cross-sell/cross-sell.categories";
import type { CrossSellProduct } from "~/features/checkout/cross-sell/cross-sell.products";
import {
	FULFILLMENT_GROUP_KIND,
	type FulfillmentGroupKind,
} from "~/features/checkout/delivery/fulfillment-groups.resolver";
import type {
	FiltersConfig,
	FiltersState,
} from "~/features/products/filters/types";
import type { SessionUser } from "~/tooling/session";
import {
	buildCheckoutStepCommerceFromCart,
	buildCheckoutStepCommerceFromOrder,
} from "~/tooling/telemetry/checkout-step-payload";
import {
	CHECKOUT_STEP_TO_TELEMETRY_CHECKOUT_STEP,
	type CheckoutTelemetryFlow,
	getNormalizedCheckoutStepNumber,
	type TELEMETRY_CHECKOUT_STEP,
} from "~/tooling/telemetry/checkout-step-sequence";
import { getImageSource } from "~/utils/default-image";
import { getProductDetailsUrl } from "~/utils/routes";
import type { ErrorTelemetry } from "./error-telemetry";

export type { CheckoutTelemetryFlow, TELEMETRY_CHECKOUT_STEP };
export { CHECKOUT_STEP_TO_TELEMETRY_CHECKOUT_STEP };

export interface BaseTrackingEvent<T = Record<string, unknown>> {
	event: string;
	ev_type:
		| "navigation"
		| "page"
		| "interaction"
		| "special_interaction"
		| "ecommerce"
		| "identify"
		| "reset";
	ev_vendor: "rudderstack";
	properties?: T;
	_clear: true;
}

const rudderstackEventBase = {
	ev_vendor: "rudderstack",
} as const;

type EventParams<T> = {
	event: string;
	ev_type: BaseTrackingEvent["ev_type"];
	properties?: T;
};

type EventConstructor<T> = () => EventParams<T>;

function createEvent<T>(
	eventConstructor: EventConstructor<T>,
): BaseTrackingEvent<T> | null {
	try {
		const { event, ev_type, properties } = eventConstructor();

		return {
			...rudderstackEventBase,
			event,
			ev_type,
			properties: {
				...properties,
				service_environment: "CANNABIS",
			} as T,
			_clear: true,
		};
	} catch (error) {
		Sentry.captureException(error);
		return null;
	}
}

export function createLoginEvent(): BaseTrackingEvent | null {
	return createEvent(() => ({
		event: "Logged In",
		ev_type: "navigation",
	}));
}

export function userLoggedOutEvent(): BaseTrackingEvent | null {
	return createEvent(() => ({
		event: "Logged Out",
		ev_type: "navigation",
	}));
}

export function userRegisteredEvent(): BaseTrackingEvent | null {
	return createEvent(() => ({
		event: "Registered",
		ev_type: "navigation",
	}));
}

export function productsSearchedEvent(query: string): BaseTrackingEvent | null {
	return createEvent(() => ({
		event: "Products Searched",
		ev_type: "ecommerce",
		properties: {
			query: query.trim(),
		},
	}));
}

/**
 * The product fields the ecommerce events read.
 *
 * Declared structurally because two cart shapes reach these events. The
 * storefront cart carries the catalogue's `pzn` and producer; the checkout
 * cart's lines are slimmer and supply neither (OTC-120), so `sku` and `brand`
 * drop out of the payload there rather than being invented.
 */
type TelemetryCartProduct = {
	id: string | number;
	name: string;
	kind: string | null;
	price?: number | null;
	category?: string | null;
	imageUrl?: string | null;
	pzn?: string | null;
	producerName?: string | null;
};

type TelemetryCartLine = {
	quantity: number;
	product: TelemetryCartProduct;
};

const mapCartItems = (
	cartItems: readonly TelemetryCartLine[],
	currency: string,
) => {
	return cartItems.map((item) => ({
		quantity: item.quantity,
		product_id: item.product.id,
		sku: item.product.pzn,
		variant: item.product.kind,
		category: item.product.category,
		name: item.product.name,
		brand: item.product.producerName,
		price: !isNil(item.product?.price) ? item.product.price / 100 : null,
		currency,
		url: `${window.location.origin}${getProductDetailsUrl(String(item.product.id), item.product.name, item.product.kind)}`,
		image_url: getProductImageUrl(item.product?.imageUrl),
	}));
};

export function productAddedToCartEvent({
	product,
	productVendor,
	currency,
	quantity,
	cartId,
	cartItems,
}: {
	product: Cart["items"][number]["product"];
	productVendor: Cart["vendor"];
	currency: string;
	quantity: number;
	cartId: Cart["cartId"];
	cartItems: Cart["items"];
}): BaseTrackingEvent | null {
	return createEvent(() => ({
		event: "Product Added",
		ev_type: "ecommerce",
		properties: {
			cart_id: String(cartId),
			quantity,
			product_id: product.id,
			vendor_id: productVendor?.id,
			vendor_name: productVendor?.name,
			sku: product.pzn,
			variant: product.kind,
			category: product.category,
			name: product.name,
			brand: product.producerName,
			price: !isNil(product?.price) ? product.price / 100 : null,
			currency,
			url: window.location.href,
			image_url: getProductImageUrl(product.imageUrl),
			cart: mapCartItems(cartItems, currency),
		},
	}));
}

export function productRemovedFromCartEvent({
	product,
	productVendor,
	currency,
	quantity,
	cartId,
	cartItems,
}: {
	product: TelemetryCartProduct;
	productVendor: Cart["vendor"];
	currency: string;
	quantity: number;
	cartId: Cart["cartId"];
	cartItems: readonly TelemetryCartLine[];
}): BaseTrackingEvent | null {
	return createEvent(() => ({
		event: "Product Removed",
		ev_type: "ecommerce",
		properties: {
			cart_id: String(cartId),
			quantity,
			product_id: product.id,
			vendor_id: productVendor?.id,
			vendor_name: productVendor?.name,
			sku: product.pzn,
			variant: product.kind,
			category: product.category,
			name: product.name,
			brand: product.producerName,
			price: !isNil(product?.price) ? product.price / 100 : null,
			currency,
			url: window.location.href,
			image_url: getProductImageUrl(product.imageUrl),
			cart: mapCartItems(cartItems, currency),
		},
	}));
}

export const pageViewedEvent = (name: string) =>
	createEvent(() => ({
		event: "Page",
		ev_type: "page",
		properties: { name },
	}));

export const productViewedEvent = (
	product: Product,
	productVendor: ProductVendorListItem | null,
	currency: string,
) =>
	createEvent(() => ({
		event: "Product Viewed",
		ev_type: "ecommerce",
		properties: {
			product_id: product.id,
			vendor_id: productVendor?.id,
			vendor_name: productVendor?.name,
			sku: productVendor?.pzn,
			variant: product.kind,
			category: product.category,
			name: product.name,
			brand: product.producer.name,
			price: !isNil(productVendor?.price) ? productVendor.price / 100 : null,
			currency,
			url: window.location.href,
			image_url: getProductImageUrl(product.imageUrl),
		},
	}));

const deliveryMethodMap: { [key in DeliveryMethodKind]: string } = {
	[DELIVERY_METHOD_KIND.SHIPPING]: "MAIL",
	[DELIVERY_METHOD_KIND.PICKUP]: "PICKUP",
	[DELIVERY_METHOD_KIND.COURIER]: "COURIER",
	[DELIVERY_METHOD_KIND.EXPRESS_COURIER]: "EXPRESS_COURIER",
};
/**
 * `cartType` is required rather than optional so a new caller has to say which
 * package it is selecting for. A mixed basket makes two selections in one
 * checkout, and without it the two events are identical -- which is the gap this
 * property exists to close. The values are the fulfillment-group kinds, so there
 * is no mapping to drift.
 */
export const deliveryMethodSelectedEvent = (
	deliveryMethodKind: DeliveryMethodKind,
	cartType: FulfillmentGroupKind,
) =>
	createEvent(() => ({
		event: "Shipping Method Selected",
		ev_type: "special_interaction",
		properties: {
			shipping_method: deliveryMethodMap[deliveryMethodKind],
			cart_type: cartType,
		},
	}));

/**
 * Which way the customer chose to pay: `services` charges the online service now
 * and the medication at the pharmacy, `combined` charges both together. The
 * values are the PAYMENT_TYPE enum the order is written with, so the event and
 * the order agree without a mapping.
 */
export const paymentMethodSelectedEvent = (
	paymentMethod: "services" | "combined",
) =>
	createEvent(() => ({
		event: "Payment Method Selected",
		ev_type: "special_interaction",
		properties: {
			payment_method: paymentMethod,
		},
	}));

export const getProductImageUrl = (imageUrl?: string | null): string => {
	if (imageUrl && !imageUrl.includes("://")) {
		return `${window.location.origin}${imageUrl}`;
	}

	return imageUrl ?? "";
};

const LIST_IDS = {
	MAIN_PRODUCT_LIST: "main_product_list",
	CROSS_SELL: "cross_sell",
} as const;

/**
 * `list_id` marks the checkout cross-sell sheet as the source, so these views
 * stay apart from the storefront product page's (OTC-324). `product_id` and
 * `category` match the cross-sell `Product Added`, so the two join per product.
 */
export const crossSellProductViewedEvent = (
	product: CrossSellProduct,
	currency: string,
) =>
	createEvent(() => ({
		event: "Product Viewed",
		ev_type: "ecommerce",
		properties: {
			product_id: Number(product.id),
			vendor_id: product.vendorId,
			category: CATEGORIES.CANNABIS_ACCESSORIES,
			name: product.name,
			brand: product.brand,
			price: product.priceCents / 100,
			currency,
			url: window.location.href,
			image_url: getProductImageUrl(product.imageUrl),
			list_id: LIST_IDS.CROSS_SELL,
			cross_sell_category: product.category
				? CROSS_SELL_CATEGORY_SLUGS[product.category]
				: null,
		},
	}));

export const productClickedEvent = (
	product: Product,
	currency: string,
	listIndex: number,
	listName?: string,
) =>
	createEvent(() => ({
		event: "Product Clicked",
		ev_type: "ecommerce",
		properties: {
			product_id: product.id,
			variant: product.kind,
			category: product.category,
			name: product.name,
			brand: product.producer.name,
			price: !isNil(product?.minPrice) ? product?.minPrice / 100 : null,
			currency,
			url: `${window.location.origin}${getProductDetailsUrl(String(product.id), product.name, product.kind)}`,
			image_url: getProductImageUrl(
				getImageSource(product.imageUrl, product.category),
			),
			list_id: listName ?? LIST_IDS.MAIN_PRODUCT_LIST, // ID of the product list being viewed (we have recommended or main)
			position: listIndex, // Position in the list
		},
	}));

export const cartProductClickedEvent = (
	product: Cart["items"][number]["product"],
	currency: string,
	listIndex: number,
) =>
	createEvent(() => ({
		event: "Product Clicked",
		ev_type: "ecommerce",
		properties: {
			product_id: product.id,
			variant: product.kind,
			category: product.category,
			name: product.name,
			brand: product.producerName,
			price: !isNil(product?.price) ? product.price / 100 : null,
			currency,
			url: `${window.location.origin}${getProductDetailsUrl(String(product.id), product.name, product.kind)}`,
			image_url: getProductImageUrl(product.imageUrl),
			list_id: "cart_drawer",
			position: listIndex,
		},
	}));

/**
 * Each line carries the kind of the package it ships in, so a basket holding
 * both can be read apart.
 *
 * Derived from the groups rather than from the product's category: category
 * describes the product, while this describes which vendor package the line
 * belongs to, and those are not the same question once a basket is mixed. A line
 * in no group reports `medication`, which is what the basket was before
 * fulfillment groups existed and what an un-grouped line still ships as.
 */
const mapCartItemsWithType = (
	items: Cart["items"],
	fulfillmentGroups: Cart["fulfillmentGroups"],
	currency: string,
) => {
	const kindByItemId = new Map<string, FulfillmentGroupKind>();
	for (const group of fulfillmentGroups) {
		for (const itemId of group.itemIds) {
			kindByItemId.set(itemId, group.kind);
		}
	}

	return mapCartItems(items, currency).map((line, index) => ({
		...line,
		cart_type: kindByItemId.get(String(items[index]?.id)) ?? "medication",
	}));
};

export const cartViewedEvent = (
	cartId: number | null,
	items: Cart["items"],
	vendor: Cart["vendor"],
	currency: string,
	fulfillmentGroups: Cart["fulfillmentGroups"] = [],
) => {
	return createEvent(() => ({
		event: "Cart Viewed",
		ev_type: "ecommerce",
		properties: {
			cart_id: String(cartId),
			vendor_id: vendor?.id,
			vendor_name: vendor?.name,
			currency,
			cart: mapCartItemsWithType(items, fulfillmentGroups, currency),
		},
	}));
};

export const checkoutStartedEvent = (cart: Cart, currency: string) => {
	const products = mapCartItems(cart.items, currency);
	return createEvent(() => ({
		event: "Checkout Started",
		ev_type: "ecommerce",
		properties: {
			value: !isNil(cart.totalPrice) ? cart.totalPrice / 100 : null,
			currency,
			pharmacy: cart.vendor?.name ?? null,
			vendor_name: cart.vendor?.name,
			vendor_id: cart.vendor?.id,
			products,
			cart: products,
		},
	}));
};

export type CheckoutStepCommerceInput = {
	cart?: Parameters<typeof buildCheckoutStepCommerceFromCart>[0];
	order?: CustomerOrderDetails;
	currency?: string;
	paymentMethod?: string;
	/** Funnel variant for 1-based step numbering. Defaults to initial. */
	flow?: CheckoutTelemetryFlow;
};

function resolveCheckoutStepCommerce(
	commerce?: CheckoutStepCommerceInput,
): ReturnType<typeof buildCheckoutStepCommerceFromCart> {
	if (!commerce?.currency) {
		return null;
	}
	if (commerce.order) {
		return buildCheckoutStepCommerceFromOrder(
			commerce.order,
			commerce.currency,
			commerce.paymentMethod,
		);
	}
	return buildCheckoutStepCommerceFromCart(commerce.cart, commerce.currency);
}

function resolveTelemetryStepNumber(
	stepTitle: TELEMETRY_CHECKOUT_STEP,
	flow?: CheckoutTelemetryFlow,
): string {
	return String(getNormalizedCheckoutStepNumber(stepTitle, flow ?? "initial"));
}

export const checkoutStepViewedEvent = (event: {
	step?: CheckoutStep;
	telemetryStep?: TELEMETRY_CHECKOUT_STEP;
	commerce?: CheckoutStepCommerceInput;
}) => {
	const currentStep = event.step
		? CHECKOUT_STEP_TO_TELEMETRY_CHECKOUT_STEP[event.step]
		: event.telemetryStep;

	if (!currentStep) {
		return null;
	}

	const commerceProperties = resolveCheckoutStepCommerce(event.commerce);

	return createEvent(() => ({
		event: "Checkout Step Viewed",
		ev_type: "ecommerce",
		properties: {
			step: resolveTelemetryStepNumber(currentStep, event.commerce?.flow),
			step_title: currentStep,
			...commerceProperties,
		},
	}));
};

// tracking team considers payment as part of he checkout process
export const checkoutStepCompletedEvent = (
	stepNavigationEvent?: {
		completedSteps: CheckoutStep[];
		currentStep: CheckoutStep;
		nextStep: CheckoutStep;
	},
	trackingCheckoutStep?: TELEMETRY_CHECKOUT_STEP,
	commerce?: CheckoutStepCommerceInput,
) => {
	if (!stepNavigationEvent && !trackingCheckoutStep) {
		return null;
	}

	let lastCompletedTrackingCheckoutStep = trackingCheckoutStep;
	if (stepNavigationEvent) {
		const nextTrackingCheckoutStep =
			CHECKOUT_STEP_TO_TELEMETRY_CHECKOUT_STEP[stepNavigationEvent.nextStep];
		lastCompletedTrackingCheckoutStep =
			CHECKOUT_STEP_TO_TELEMETRY_CHECKOUT_STEP[stepNavigationEvent.currentStep];
		// Skip only when current === next (same URL remount / no-op) or unmapped.
		if (
			!nextTrackingCheckoutStep ||
			!lastCompletedTrackingCheckoutStep ||
			lastCompletedTrackingCheckoutStep === nextTrackingCheckoutStep
		) {
			return null;
		}
	}

	if (!lastCompletedTrackingCheckoutStep) {
		return null;
	}

	const commerceProperties = resolveCheckoutStepCommerce(commerce);

	return createEvent(() => ({
		event: "Checkout Step Completed",
		ev_type: "ecommerce",
		properties: {
			step: resolveTelemetryStepNumber(
				lastCompletedTrackingCheckoutStep,
				commerce?.flow,
			),
			step_title: lastCompletedTrackingCheckoutStep,
			...commerceProperties,
		},
	}));
};

type QuestionnairePrefillEventProperties = {
	complaint: string;
	orderCount: number;
	reason?: "no" | "close";
};

export const questionnairePrefillOfferedEvent = ({
	complaint,
	orderCount,
}: QuestionnairePrefillEventProperties) =>
	createEvent(() => ({
		event: "questionnaire_prefill_offered",
		ev_type: "special_interaction",
		properties: {
			complaint,
			orderCount,
		},
	}));

export const questionnairePrefillAcceptedEvent = ({
	complaint,
	orderCount,
}: QuestionnairePrefillEventProperties) =>
	createEvent(() => ({
		event: "questionnaire_prefill_accepted",
		ev_type: "special_interaction",
		properties: {
			complaint,
			orderCount,
		},
	}));

export const questionnairePrefillDeclinedEvent = ({
	complaint,
	orderCount,
	reason,
}: QuestionnairePrefillEventProperties) =>
	createEvent(() => ({
		event: "questionnaire_prefill_declined",
		ev_type: "special_interaction",
		properties: {
			complaint,
			orderCount,
			reason,
		},
	}));

// Express Processing VAS (€4.99 prescription review upsell). Mirrors mobile's
// pre-2026-03 contract (`has_express_checkout` / `total_express_checkout` on
// `Order Completed`) so the warehouse `dr_ansay_production.order_completed`
// columns repopulate from web — see MKP-1505. Always emits both fields
// (`false` / `0` when absent) so analytics can `COUNTIF` without null guards.
type OrderVasItem = NonNullable<CustomerOrderDetails["vasItems"]>[number];

const EXPRESS_PROCESSING_VAS_TYPE: OrderVasItem["type"] = "express_processing";

const sumExpressVasCents = (
	vasItems: CustomerOrderDetails["vasItems"],
): number =>
	(vasItems ?? [])
		.filter((vasItem) => vasItem.type === EXPRESS_PROCESSING_VAS_TYPE)
		.reduce((sum, vasItem) => sum + vasItem.price, 0);

export const orderCompletedEvent = (
	order: CustomerOrderDetails,
	paymentDetails: { paymentMethod: string; currency: string },
) => {
	return createEvent(() => {
		// The whole checkout, not just the child order this page was routed to.
		//
		// orderTotal is computed from a single vendor_order, so a mixed basket omits
		// whatever the accessories order was charged and revenue reads low. The
		// parent records what the checkout's single PSP session actually charged,
		// which is the number this field was always meant to carry.
		//
		// Not a redefinition for anything that came before: a single-vendor checkout
		// has one child and the parent total equals it, and orders predating parent
		// orders have no parent at all and fall through unchanged. The value differs
		// only where it was already wrong.
		const total = Math.max(
			0,
			(order.parentTotalAmount ?? order.orderTotal) / 100,
		);
		let discount = order.discount;
		if (order.prescriptionCost === 0) {
			// first order should be tracked as a discount
			discount = order.voucher?.code
				? order.discount + PRICES.onlineService
				: PRICES.onlineService;
		}

		const expressVasTotalCents = sumExpressVasCents(order.vasItems);

		// Every line of the checkout, each carrying the kind of the package it
		// shipped in. siblingVendorOrders answers with every package, this one
		// included, so the reporting order's own package is filtered out: taking it
		// as well would list its lines twice and inflate the attachment rate this
		// event is measured for. Its lines come from `order.items`, the field a
		// single-vendor checkout already reported, so that payload is untouched.
		// An order placed before parent orders existed has no package list and is
		// not backfilled. Every one of them is a pharmacy order, so its lines are
		// the medication package's -- which is also what derivePackageKind answers
		// for a package it cannot read a category from.
		const ownCartType =
			order.vendorOrders?.find((child) => child.orderId === order.orderId)
				?.cartType ?? FULFILLMENT_GROUP_KIND.MEDICATION;
		const lines = [
			...order.items.map((item) => ({ item, cartType: ownCartType })),
			...(order.vendorOrders ?? [])
				.filter((child) => child.orderId !== order.orderId)
				.flatMap((child) =>
					child.items.map((item) => ({ item, cartType: child.cartType })),
				),
		];

		return {
			event: "Order Completed",
			ev_type: "ecommerce",
			properties: {
				order_id: order.orderId,
				// Joins the child orders one checkout wrote. Null for an order placed
				// before parent orders existed, which is how a consumer tells a
				// single-order checkout from one it has only half of.
				parent_order_id: order.parentOrderId ?? null,
				// One entry per vendor package, so a mixed checkout is legible without
				// a second event. Null rather than a one-entry array when there is no
				// parent, so its absence is distinguishable from a single-vendor
				// checkout that genuinely has one package.
				vendor_orders:
					order.vendorOrders?.map((child) => ({
						order_id: child.orderId,
						suggested_order_id: child.suggestedOrderId,
						vendor_id: child.vendorId,
						vendor_name: child.vendorName,
						cart_type: child.cartType,
						shipping_method: child.deliveryMethodKind
							? deliveryMethodMap[child.deliveryMethodKind]
							: null,
						vendor_cost: Math.max(0, (child.vendorCost ?? 0) / 100),
						shipping_cost: Math.max(0, (child.shippingCost ?? 0) / 100),
					})) ?? null,
				shipping_method: deliveryMethodMap[order.deliveryMethod.kind], // "MAIL" | "PICKUP" | "COURIER" | "EXPRESS_COURIER"
				total, //Revenue with the discount and coupons factored in
				discount: discount / 100,
				coupon: order.voucher?.code ?? null,
				pharmacy: order.vendor.name ?? null,
				vendor_id: order.vendor.id, // should be the internal ID of the selected vendor that the price corresponds to
				vendor_name: order.vendor.name,
				currency: paymentDetails.currency,
				has_videocall: order.withVideoCall ?? false,
				total_videocall: Math.max(0, order.videoCallCost / 100),
				has_express_checkout: expressVasTotalCents > 0,
				total_express_checkout: Math.max(0, expressVasTotalCents / 100),
				products: lines.map(({ item, cartType }) => ({
					// The product, not the order line. `mapCartItems` already reports it
					// this way for Cart Viewed and Checkout Started; this event was left
					// out of that change (TMR-392) and kept sending the line id, which
					// never repeats across orders and so grouped nothing.
					product_id: item.productId,
					sku: item.pzn,
					brand: item.producerName,
					variant: item.kind,
					category: item.category,
					name: item.name,
					price: !isNil(item.price) ? item.price / 100 : null,
					currency: paymentDetails.currency,
					quantity: item.quantity,
					url: `${window.location.origin}${getProductDetailsUrl(String(item.productId), item.name, item.kind)}`,
					image_url: getProductImageUrl(item.imageUrl),
					cart_type: cartType,
				})),
			},
		};
	});
};

export function userIdentifiedEvent(
	user: SessionUser,
	customer: Omit<CurrentCustomer, "coordinates">,
): BaseTrackingEvent | null {
	return createEvent(() => ({
		event: "Identify",
		ev_type: "identify",
		properties: {
			userId: user.externalId,
			address: {
				city: customer.city,
				country: customer.country,
				postalCode: customer.postalCode,
			},
			email: customer.emailAddress,
			firstname: customer.firstName,
			phone: customer.mobile,
		},
	}));
}

export function userResetEvent(): BaseTrackingEvent | null {
	return createEvent(() => ({
		event: "Reset",
		ev_type: "reset",
	}));
}

export function couponAppliedEvent({
	cartId,
	code,
	discountAmount,
	voucherDetails,
}: {
	cartId: string;
	code: string;
	discountAmount: number;
	voucherDetails: VoucherDetails;
}): BaseTrackingEvent | null {
	return createEvent(() => ({
		event: "Coupon Applied",
		ev_type: "ecommerce",
		properties: {
			cart_id: String(cartId),
			coupon_name: code,
			discount_type:
				voucherDetails.discountType === "PERCENT" ? "percentage" : "absolute",
			discount:
				voucherDetails.discountType === "PERCENT"
					? voucherDetails.percentOff
					: discountAmount / 100,
		},
	}));
}

const productSortMap: Record<ProductsSortableField, string> = {
	createdAt: "new",
	thc: "thc",
	rating: "best rated",
	popularity: "popular",
	price: "price",
	recommended: "recommended",
	distance: "nearest",
};

export function productListViewedEvent(
	products: Product[],
	listParams: {
		filters: FiltersState | null;
		search: string | null;
		sort: ProductsSortableField | null;
		order: ProductsSortOrder | null;
	},
	currency: string,
	startPosition: number,
	filtersConfig: FiltersConfig,
): BaseTrackingEvent | null {
	const { filters, search, sort, order } = listParams;

	return createEvent(() => ({
		event: "Product List Viewed",
		ev_type: "ecommerce",
		properties: {
			query: search,
			products: products.map((product, i) => ({
				product_id: product.id,
				category: product.category,
				name: product.name,
				brand: product.producer.name,
				variant: product.kind,
				price: !isNil(product.minPrice) ? product.minPrice / 100 : null,
				currency,
				url: `${window.location.origin}${getProductDetailsUrl(product.id, product.name, product.kind)}`,
				image_url: getProductImageUrl(
					getImageSource(product.imageUrl, product.category),
				),
				list_id: LIST_IDS.MAIN_PRODUCT_LIST,
				position: startPosition + i,
			})),
			filters: filters ? mapFiltersToAnalytics(filters, filtersConfig) : null,
			sorts: [
				...(!!sort && !!order
					? [
							{
								type: productSortMap[sort],
								value: order,
							},
						]
					: []),
			],
		},
	}));
}

type PrimitiveFilterValue = string | number | boolean;
type FilterValue =
	| PrimitiveFilterValue
	| PrimitiveFilterValue[]
	| { id: PrimitiveFilterValue; name: string }[];

function mapFiltersToAnalytics(
	filters: FiltersState,
	filtersConfig: FiltersConfig,
): Array<{
	type: string;
	value: FilterValue;
}> {
	const mappedFilters: Array<{
		type: string;
		value: FilterValue;
	}> = [];

	// Custom filter type name mappings
	const filterTypeNameMap: Record<string, string> = {
		kinds: "genetics",
	};
	const filterValueMap: Record<
		string,
		(value: PrimitiveFilterValue | PrimitiveFilterValue[]) => FilterValue
	> = {
		category: (value) => {
			if (typeof value !== "number") return value;

			return (
				filtersConfig.categories.find((category) => category.id === value)
					?.name ?? ""
			);
		},
		producers: (value) => {
			if (!Array.isArray(value)) return value;

			return value.map((id) => {
				const producer = filtersConfig.producers.find(
					(producer) => producer.id === id,
				);
				return { id, name: producer?.name ?? "" };
			});
		},
	};

	// Process all filter properties generically
	Object.entries(filters).forEach(([filterKey, filterValue]) => {
		// Skip empty or undefined values
		if (!filterValue) return;

		// Geo is the only object-valued filter and is not part of the event.
		if (typeof filterValue === "object" && !Array.isArray(filterValue)) return;

		const filterType = filterTypeNameMap[filterKey] || filterKey;
		const filterValueMapper = filterValueMap[filterKey];
		const value = filterValueMapper?.(filterValue) ?? filterValue;

		mappedFilters.push({
			type: filterType,
			value,
		});
	});

	return mappedFilters;
}

export function productListFilteredEvent(listParams: {
	filters: FiltersState | null;
	sort: ProductsSortableField | null;
	order: ProductsSortOrder | null;
	filtersConfig: FiltersConfig;
}): BaseTrackingEvent | null {
	const { filters, sort, order, filtersConfig } = listParams;

	return createEvent(() => ({
		event: "Product List Filtered",
		ev_type: "ecommerce",
		properties: {
			filters: filters ? mapFiltersToAnalytics(filters, filtersConfig) : null,
			sorts: [
				...(!!sort && !!order
					? [
							{
								type: productSortMap[sort],
								value: order,
							},
						]
					: []),
			],
		},
	}));
}

export function productListFiltersRemovedEvent(
	filters: FiltersState,
	filtersConfig: FiltersConfig,
	removedAllFilters = false,
): BaseTrackingEvent | null {
	return createEvent(() => ({
		event: "Product List Filters Removed",
		ev_type: "special_interaction",
		properties: {
			filters: mapFiltersToAnalytics(filters, filtersConfig),
			remove_all: removedAllFilters,
		},
	}));
}

const createPromotionEvent = ({
	promotionId,
	creative,
	position,
	currency,
	products,
	name,
}: {
	promotionId: string;
	name: string;
	creative?: string;
	position: string | number;
	products: Product[];
	currency: string;
}) => ({
	properties: {
		promotion_id: promotionId,
		name,
		creative,
		position,
		products: products.map((product) => ({
			product_id: product.id,
			variant: product.kind,
			category: product.category,
			name: product.name,
			brand: "producer" in product ? product.producer.name : null,
			price: !isNil(product.minPrice) ? product.minPrice / 100 : null,
			currency,
			url: `${window.location.origin}${getProductDetailsUrl(
				String(product.id),
				product.name,
				product.kind,
			)}`,
			image_url: getProductImageUrl(
				product.category
					? getImageSource(product.imageUrl, product.category)
					: product.imageUrl,
			),
			promotion_id: promotionId,
			promotion_name: name,
			creative,
			position,
		})),
	},
});

export function promotionViewedEvent({
	promotionId,
	name,
	creative,
	position,
	products,
	currency,
}: {
	promotionId: string;
	name: string;
	creative?: string;
	position: string | number;
	products: Product[];
	currency: string;
}): BaseTrackingEvent | null {
	return createEvent(() => ({
		...createPromotionEvent({
			promotionId,
			name,
			creative,
			position,
			products,
			currency,
		}),
		event: "Promotion Viewed",
		ev_type: "ecommerce",
	}));
}

export function promotionClickedEvent({
	promotionId,
	name,
	creative,
	position,
	products,
	currency,
}: {
	promotionId: string;
	name: string;
	creative?: string;
	position: string | number;
	products: Product[];
	currency: string;
}): BaseTrackingEvent | null {
	return createEvent(() => ({
		...createPromotionEvent({
			promotionId,
			name,
			creative,
			position,
			products,
			currency,
		}),
		event: "Promotion Clicked",
		ev_type: "ecommerce",
	}));
}

export function pharmacySearchedEvent(query: string): BaseTrackingEvent | null {
	return createEvent(() => ({
		event: "Pharmacy Searched",
		ev_type: "special_interaction",
		properties: {
			query: query.trim(),
		},
	}));
}

export function noResultsSearchedEvent(
	type: "product" | "pharmacy",
	query: string,
): BaseTrackingEvent | null {
	return createEvent(() => ({
		event: "Zero Results Searched",
		ev_type: "special_interaction",
		properties: {
			type,
			query: query.trim(),
		},
	}));
}

export function userDataChangedEvent(
	fieldName: string,
	fieldKey: string,
): BaseTrackingEvent | null {
	return createEvent(() => ({
		event: "User Data Changed",
		ev_type: "special_interaction",
		properties: {
			field_name: fieldName,
			field_key: fieldKey,
		},
	}));
}

export function imageUploadedEvent(
	imageType: "personal_id" | "prescription",
	imageSize: number,
	fileType: string,
): BaseTrackingEvent | null {
	return createEvent(() => ({
		event: "Image Uploaded",
		ev_type: "special_interaction",
		properties: {
			image_type: imageType,
			image_size: imageSize,
			file_type: fileType,
		},
	}));
}

export function navigationClickedEvent(
	elementId: string,
	elementText: string,
	navigationLevel: "main" | "sub",
): BaseTrackingEvent | null {
	return createEvent(() => ({
		event: "Navigation Clicked",
		ev_type: "navigation",
		properties: {
			element_id: elementId,
			element_text: elementText,
			navigation_type: navigationLevel,
		},
	}));
}

/**
 * Measurement Plan `cta_click`. `elementText` carries the label the user sees
 * (resolved translation), `elementId` a fixed, locale-independent identifier.
 */
export function ctaClickedEvent(
	elementId: string,
	elementText: string,
	linkUrl?: string,
): BaseTrackingEvent | null {
	return createEvent(() => ({
		event: "CTA Clicked",
		ev_type: "navigation",
		properties: {
			element_id: elementId,
			element_text: elementText,
			...(linkUrl === undefined ? {} : { link_url: linkUrl }),
		},
	}));
}

/**
 * Impression half of `ctaClickedEvent`: `bannerId` is the CTA's own `element_id`,
 * so viewed → clicked is one join rather than a mapping (MS-1180).
 */
export function bannerViewedEvent(bannerId: string): BaseTrackingEvent | null {
	return createEvent(() => ({
		event: "Banner Viewed",
		ev_type: "interaction",
		properties: {
			banner_id: bannerId,
			// Without the query: the order id must not reach GA4 or Facebook (MS-1050).
			url: `${window.location.origin}${window.location.pathname}`,
		},
	}));
}

/** Measurement Plan `link_click`. Same value contract as `ctaClickedEvent`. */
export function linkClickedEvent(
	elementId: string,
	linkText: string,
): BaseTrackingEvent | null {
	return createEvent(() => ({
		event: "Link Clicked",
		ev_type: "navigation",
		properties: {
			element_id: elementId,
			link_text: linkText,
		},
	}));
}

export function pharmacySelectedEvent(
	pharmacyId: string,
	pharmacyName: string,
	pharmacyType: "all" | "top",
	deliveryMethodKind: DeliveryMethodKind,
): BaseTrackingEvent | null {
	return createEvent(() => ({
		event: "Pharmacy Selected",
		ev_type: "special_interaction",
		properties: {
			pharmacy_id: pharmacyId,
			pharmacy_name: pharmacyName,
			pharmacy_type:
				pharmacyType === "top"
					? `Top ${capitalize(deliveryMethodKind)} Pharmacy`
					: `All ${capitalize(deliveryMethodKind)} Pharmacies`,
		},
	}));
}

export function cartEmptiedEvent(
	cartId: Cart["cartId"],
): BaseTrackingEvent | null {
	return createEvent(() => ({
		event: "Cart Emptied",
		ev_type: "ecommerce",
		properties: {
			cart_id: String(cartId),
			cart: [],
		},
	}));
}

export function profileModalOpenedEvent(
	isAuthenticated: boolean,
): BaseTrackingEvent | null {
	return createEvent(() => ({
		event: "Profile Modal Opened",
		ev_type: "special_interaction",
		properties: {
			auth: isAuthenticated,
		},
	}));
}

/** MKP-1758: DrAnsay Pay promo banner Learn more CTA in user profile menu. */
export function drAnsayPayLearnMoreClickedEvent(
	isAuthenticated: boolean,
): BaseTrackingEvent | null {
	return createEvent(() => ({
		event: "DrAnsay Pay Learn More Clicked",
		ev_type: "special_interaction",
		properties: {
			auth: isAuthenticated,
			banner_location: "user_profile_menu",
		},
	}));
}

/** MKP-1758: DrAnsay Pay education overlay opened from profile menu. */
export function drAnsayPayInfoOverlayOpenedEvent(
	isAuthenticated: boolean,
): BaseTrackingEvent | null {
	return createEvent(() => ({
		event: "DrAnsay Pay Info Overlay Opened",
		ev_type: "special_interaction",
		properties: {
			auth: isAuthenticated,
		},
	}));
}

export function servicesLinkClickedEvent(
	linkId: string,
	isAuthenticated: boolean,
): BaseTrackingEvent | null {
	return createEvent(() => ({
		event: "Services Link Clicked",
		ev_type: "navigation",
		properties: {
			auth: isAuthenticated,
			id: linkId,
		},
	}));
}

export function logoutSuccessEvent(): BaseTrackingEvent | null {
	return createEvent(() => ({
		event: "Logout Success",
		ev_type: "navigation",
		properties: {
			auth: true,
		},
	}));
}

export function modalClosedEvent(
	isAuthenticated: boolean,
): BaseTrackingEvent | null {
	return createEvent(() => ({
		event: "Modal Closed",
		ev_type: "special_interaction",
		properties: {
			auth: isAuthenticated,
		},
	}));
}

export function followUpPrescriptionBannerClickedEvent(): BaseTrackingEvent | null {
	return createEvent(() => ({
		event: "Follow-up Prescription Banner Clicked",
		ev_type: "special_interaction",
		properties: {
			banner_location: "checkout_payment_success",
		},
	}));
}

type WarningTrigger = "manufacturer" | "pharmacy";
type WarningAction = "shown" | "undo" | "dismissed";

export function errorViewedEvent(
	errorTrace: string | undefined,
	errorMessage: string,
	telemetry?: ErrorTelemetry,
): BaseTrackingEvent | null {
	return createEvent(() => ({
		event: "Error Viewed",
		ev_type: "ecommerce",
		properties: {
			error_trace: errorTrace ?? null,
			error_message: errorMessage,
			error_category: telemetry?.category ?? "unknown",
			error_code: telemetry?.codes?.[0] ?? null,
			error_codes: telemetry?.codes ?? [],
		},
	}));
}

/** Tracks light-friction warning prompt interactions (MKP-346). */
export function filterWarningEvent(
	trigger: WarningTrigger,
	action: WarningAction,
): BaseTrackingEvent | null {
	return createEvent(() => ({
		event: "Filter Warning Interaction",
		ev_type: "special_interaction",
		properties: {
			trigger,
			action,
		},
	}));
}
